import { Grid, Typography } from "@mui/material";
import { useActiveFrameworkCard } from "./hooks/useActiveFrameworkCard";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { Fragment } from "react";
import { Upload } from "~/components/upload";
import { Space } from "~/lib/types";
import { Format } from "~/lib/date";
import { CircularIconButton } from "~/components/CircularIconButton/CircularIconButton";
import { IconButtonType } from "~/components/CircularIconButton/DynamicButtonIcon";
import { CanDeleteFramework } from "~/lib/iam/actions/cnspec/policy/PolicyHub/DeleteFramework";
import { CanSetBundle } from "~/lib/iam/actions/cnspec/policy/PolicyHub/SetBundle";

type Props = {
  space: Space;
  data: ReturnType<typeof useActiveFrameworkCard>["data"];
  handle: ReturnType<typeof useActiveFrameworkCard>["handle"];
  loading: ReturnType<typeof useActiveFrameworkCard>["loading"];
  isOfficial: boolean;
};

export function ActiveFrameworkCardEditTools({
  space,
  data,
  handle,
  loading,
  isOfficial,
}: Props) {
  const replaceFrameworkAdapter = async ({
    spaceMrn,
    dataurl,
  }: {
    spaceMrn: string;
    dataurl: string;
  }) => {
    await handle.replaceFramework({ spaceMrn, dataurl });
  };

  return (
    <Fragment>
      <Grid
        container
        sx={{
          height: 80,
          px: 3,
          py: 2,
          alignItems: "center",
          justifyContent: "center",
        }}
        size={12}
      >
        <Grid size="grow">
          {data.uploadedAt && (
            <Typography variant="caption" color="text.secondary">
              Uploaded {Format(data.uploadedAt)}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          size="auto"
        >
          <Grid>
            <CircularIconButton
              title="Download framework"
              iconType={IconButtonType.Download}
              onClick={handle.downloadFramework}
            />
          </Grid>
          {/* only show this if the framework is custom */}
          {!isOfficial && (
            <CanSetBundle>
              <Grid>
                <Upload
                  title="Replace framework"
                  placeholder={`Drag and drop a framework or click to replace ${data.name}`}
                  errorMessage={`Failed to replace ${data.name}`}
                  successMessage={`Successfully replaced ${data.name}`}
                  spaceMrn={space.mrn}
                  onAddFile={replaceFrameworkAdapter}
                  buttonIcon={IconButtonType.Refresh}
                  buttonTitle="Replace framework"
                  force={false}
                />
              </Grid>
            </CanSetBundle>
          )}
          <CanDeleteFramework>
            <Grid>
              <CircularIconButton
                title={`${isOfficial ? "Remove" : "Delete"} Framework`}
                iconType={IconButtonType.Delete}
                onClick={
                  isOfficial
                    ? handle.removeFrameworkBegin
                    : handle.deleteFrameworkBegin
                }
                sx={{ color: "error.main" }}
              />
            </Grid>
          </CanDeleteFramework>
          <Grid>{data.displayInteractions}</Grid>
        </Grid>
      </Grid>
      <ConfirmationDialog
        title={`${isOfficial ? "Remove" : "Delete"} framework ${data.name}?`}
        isOpen={handle.deleteDialogOpen}
        confirmButtonText={isOfficial ? "Remove" : "Delete"}
        content={
          <Typography>
            {isOfficial
              ? "Are you sure you want to remove this framework?"
              : "Deleting a custom compliance framework will erase all data – this action cannot be undone. If you are facing problems, try replacing the file first.z"}
          </Typography>
        }
        onConfirm={
          isOfficial ? handle.removeFramework : handle.deleteFrameworkConfirm
        }
        onClose={handle.cancelDelete}
        loading={loading.deleteProcess}
      />
    </Fragment>
  );
}
